import React, { useState, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationCircle, faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { toast, ToastContainer } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { loginAdmin, getLoansByAdminIdAndRole, sendOtp, verifyOtp, resendOtp, mobileOTPLogin, createOrGetCustomer } from "../middleware/backend_hepler";
import { AuthContext } from "../Context/AuthContext";
import CustomAlert from "../common/CustomAlert";
import logo from "../assets/right.png";
import ButtonLoader from "../components/ButtonLoader";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [loading, setLoading] = useState(false);

  // OTP states
  const [showOtpLogin, setShowOtpLogin] = useState(true);
  const [mobile, setMobile] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpDigits, setOtpDigits] = useState(["", "", "", ""]);
  const [isAgreed, setIsAgreed] = useState(false);

  const navigate = useNavigate();
  const { login: loginUser } = useContext(AuthContext);


  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const newErrors = { email: "", password: "" };
    if (!email) newErrors.email = "Please enter your email";
    if (!password) newErrors.password = "Please enter your password";
    setErrors(newErrors);

    if (!email || !password) {
      setLoading(false);
      return;
    }

    try {
      const response = await loginAdmin(email, password);
      if (response.token) {
        const { id, name, role } = response.admin;
        loginUser(id, name, role, response.token);

        localStorage.setItem("isLoggedIn", "true");
        localStorage.setItem("adminId", id);
        localStorage.setItem("adminName", name);
        localStorage.setItem("adminRole", role);
        localStorage.setItem("authToken", response.token);

        await getLoansByAdminIdAndRole(id, role, name);
        if (role === "customer") {
          navigate("/homeloanlist", { state: { id, role } });
        } else {
          navigate("/dashboard", { state: { id, role } });
        }
      } else {
        setLoading(false);
        throw new Error("Login failed: No token received.");
      }
    } catch (error) {
      setLoading(false);
      handleLoginError(error);
    }
  };  

  const handleLoginError = (error) => {
    const message = error.response?.data?.message || "An error occurred";
    let displayMessage;

    if (message.includes("Account is Inactive")) {
      displayMessage = "Inactive user. Please contact admin.";
    } else if (message.includes("Your agent ID will be generated shortly")) {
      displayMessage = "Your account is under verification process";
    } else {
      displayMessage = "Invalid email or password";
    }

    setAlertMessage(displayMessage);
    setShowAlert(true);
  };

  const handleAlertClose = () => setShowAlert(false);
  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  const handleSendOtp = async () => {
    try {
      setLoading(true);
      if (!mobile) {
        toast.error("Mobile number is required");
        setLoading(false);
        return;
      }
      const response = await sendOtp({ mobile });
      if (response.status === 200 && response.data?.type === "success") {
        toast.success("OTP sent successfully!");
        setOtpSent(true);
        setLoading(false);
      } else {
        toast.error("Failed to send OTP. Please try again.");
        setLoading(false);
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
    }
  };

  const handleVerifyOtp = async () => {
    setLoading(true);
    const otp = otpDigits.join("");

    if (!mobile) {
      toast.error("Mobile number is required");
      setLoading(false);
      return;
    }

    if (otp.length !== 4) {
      toast.error("Please enter a 4-digit OTP");
      setLoading(false);
      return;
    }

    try {
      // Verify OTP
      const response = await verifyOtp({ otp, mobile });

      if (response.status === 200 && response.data.type === "success") {

        // Check if exists
        let adminId, adminName, adminRole, adminMobile;
        try {
          const existingAdmin = await createOrGetCustomer({ mobile, name: null });
          adminId = existingAdmin.adminId;
          adminName = existingAdmin.name || null;
          adminRole = "customer";
          adminMobile = mobile;
        } catch (err) {
          toast.error("Error checking/creating customer: " + (err.response?.data?.message || err.message));
          setLoading(false);
          return;
        }

        // OTP Login
        const responseOTP = await mobileOTPLogin(mobile);
        if (responseOTP.token) {
          const token = responseOTP.token;
          loginUser(adminId, adminName, adminRole, token);

          localStorage.setItem("isLoggedIn", "true");
          localStorage.setItem("adminId", adminId);
          localStorage.setItem("adminName", adminName);
          localStorage.setItem("adminRole", adminRole);
          localStorage.setItem("adminMobile", adminMobile);
          localStorage.setItem("authToken", token);

          await getLoansByAdminIdAndRole(adminId, adminRole, adminName);

          navigate("/homeloanlist", { state: { id: adminId, role: adminRole } });
          setLoading(false);
        } else {
          toast.error("Login failed. Please try again.");
          setLoading(false);
        }

      } else {
        toast.error("Invalid OTP. Please try again.");
        setLoading(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    try {
      if (!mobile) {
        toast.error("Mobile number is required");
        return;
      }

      const response = await resendOtp({ mobile });
      if (response.status === 200 && response.data.type === "success") {
        toast.success("OTP resent successfully!");
      } else {
        toast.error("Failed to resend OTP. Please try again.");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
    }
  };

  const handleOtpChange = (index, value) => {
    const updatedOtp = [...otpDigits];
    updatedOtp[index] = value;
    setOtpDigits(updatedOtp);

    if (value && index < 3) {
      document.getElementById(`otp-input-${index + 1}`).focus();
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-box">
          <img src={logo} alt="Logo" className="logo" />

          {!showOtpLogin ? (
            <>
              <h2>Welcome Back!</h2>
              <p>Sign in to continue to Right Lender now.</p>
            </>
          ) : (
            <>
              <h5>Get Your Loan in 3 Simple Steps!</h5>
              <h6>Start With Mobile Number</h6>
            </>
          )}

          {!showOtpLogin ? (
            <form className="login-form" onSubmit={handleSubmit}>
              <div className={`form-group ${errors.email ? "error" : ""}`}>
                <label htmlFor="email">Email</label>
                <div className={`input-container ${errors.email ? "error" : ""}`}>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    placeholder="Enter email"
                    className="signup-field"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  {errors.email && (
                    <FontAwesomeIcon
                      icon={faExclamationCircle}
                      className="info-icon"
                    />
                  )}
                </div>
                {errors.email && <p className="error-message">{errors.email}</p>}
              </div>
              <div className={` ${errors.password ? "error" : ""}`}>
                <label htmlFor="password">Password</label>
                <div
                  className={`input-container ${errors.password ? "error" : ""}`}
                >
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    placeholder="Enter password"
                    className="signup-field"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  {!errors.password ? (
                    <FontAwesomeIcon
                      icon={showPassword ? faEye : faEyeSlash}
                      className="toggle-password-icon"
                      onClick={togglePasswordVisibility}
                    />
                  ) : (
                    <FontAwesomeIcon
                      icon={faExclamationCircle}
                      className="info-icon"
                    />
                  )}
                </div>
                {errors.password && (
                  <p className="error-message">{errors.password}</p>
                )}
              </div>
              <div className="remember-container">
                <div className="remember-me">
                  <input type="checkbox" id="remember" name="remember" />
                  <label htmlFor="remember">Remember me</label>
                </div>
                <Link
                  to="/forgot-password"
                  className="forgot-password-link"
                  state={{ email }}
                >
                  Forgot password?
                </Link>
              </div>
              {!loading ? <button type="submit">Login</button> : <ButtonLoader />}
              
              <p className="signup-text">
                Don't have an account?{" "}
                <span
                  onClick={() => setShowOtpLogin(true)}
                  className="signup-link"
                >
                  Login with OTP
                </span>
              </p>
              {loginError && <p className="error-message">{loginError}</p>}
            </form>
            ) : (
            <div className="login-form">
              {!otpSent ? (
                <>
                  <input
                    className="mobile-login-input"
                    type="text"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value)}
                    placeholder="Enter Mobile Number"
                    maxLength={10}
                  />
                  <div className="agreement-bx">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        className="custom-checkbox"
                        checked={isAgreed}
                        onChange={(e) => setIsAgreed(e.target.checked)}
                      />
                      <span className="highlight-color">
                        Read Our{" "}
                        <Link to="/terms-conditions">Terms & Conditions</Link> &{" "}
                        <Link to="/privacy-policy">Privacy Policy</Link>
                      </span>
                    </label>
                  </div>
                  
                  {!loading ? 
                    <button 
                      className="mt-2" 
                      onClick={handleSendOtp}
                      disabled={!isAgreed}
                      style={{
                        opacity: !isAgreed ? 0.5 : 1,
                        cursor: !isAgreed ? "not-allowed" : "pointer"
                      }} 
                      >
                        Get OTP
                    </button> : <ButtonLoader />}
                </>
              ) : (
                <>
                  {loading && (
                    <div className="otp-loader">
                      <ButtonLoader />
                    </div>
                  )}
                  <div className="otp-input-group">
                    {otpDigits.map((digit, index) => (
                      <input
                        key={index}
                        id={`otp-input-${index}`}
                        className="otp-input"
                        placeholder="*"
                        type="text"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                      />
                    ))}
                  </div>
                  <p>
                    Didn’t receive OTP? <span onClick={handleResendOtp} style={{ color: "#004AAD", cursor: "pointer" }}>Resend</span>
                  </p>
                  <div className="otp-buttons mb-2">
                    <button onClick={handleVerifyOtp} className="otp-submit-btn">Submit</button>
                    <button onClick={() => navigate(-1)} className="otp-cancel-btn">Cancel</button>
                  </div>
                </>
              )}
              <p className="signup-text">
                Already have an account?{" "}
                <span
                  onClick={() => setShowOtpLogin(false)}
                  className="signup-link"
                >
                  Login
                </span>
              </p>
            </div>
          )}

          {showAlert && <CustomAlert message={alertMessage} onClose={handleAlertClose} />}
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default Login;
